import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import router from "@/router";
import { AuthTokens } from "@/types/auth";
import { useAuthStore } from "@/store/auth";
import Decimal from "decimal.js";
import { DateObject } from "@/classes/datetime";

// Create a custom interface extending AxiosInstance
interface CustomAxiosInstance extends AxiosInstance {
  $request<T = any, D = any>(config: AxiosRequestConfig<D>): Promise<T>;
  $get<T = any, D = any>(
    url: string,
    config?: AxiosRequestConfig<D>,
  ): Promise<T>;
  $delete<T = any, D = any>(
    url: string,
    config?: AxiosRequestConfig<D>,
  ): Promise<T>;
  $head<T = any, D = any>(
    url: string,
    config?: AxiosRequestConfig<D>,
  ): Promise<T>;
  $options<T = any, D = any>(
    url: string,
    config?: AxiosRequestConfig<D>,
  ): Promise<T>;
  $post<T = any, D = any>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig<D>,
  ): Promise<T>;
  $put<T = any, D = any>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig<D>,
  ): Promise<T>;
  $patch<T = any, D = any>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig<D>,
  ): Promise<T>;
}

Decimal.set({ precision: 100 });

const transformResponse = (response: any) => {
  if (typeof response == "string") {
    return JSON.parse(response, (key, value) => {
      try {
        if (
          value !== null &&
          typeof value === "object" &&
          !Array.isArray(value)
        ) {
          if (value._type === "decimal") {
            return new Decimal(value.value);
          } else if (value._type === "date") {
            return DateObject.fromString(value.value);
          } else if (value._type === "datetime") {
            return new Date(value.value);
          }
        }
        return value;
      } catch (err) {
        console.error(err);
        return value;
      }
    });
  }
  return response;
};

// Setup axios instance
const axiosInstance = axios.create({
  withCredentials: true,
  baseURL: process.env.VUE_APP_API_URL,
  transformResponse: transformResponse,
}) as CustomAxiosInstance;

axiosInstance.interceptors.request.use((config) => {
  config.headers.Authorization = `Bearer ${localStorage.getItem("auth_local")}`;
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const auth = useAuthStore();
    const originalRequest = error.config;
    if (
      error.response?.status === 401 &&
      originalRequest.url !== "/auth/login" &&
      originalRequest.url !== "/auth/refresh" &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      try {
        if (!auth.state.refresh) {
          await auth.refreshToken();
        } else {
          await auth.state.refresh;
        }

        axiosInstance.defaults.headers.common.Authorization = `Bearer ${localStorage.getItem(
          "auth_local",
        )}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        auth.reset();
        router.push("/login");
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  },
);

axiosInstance.$request = async <T = any, D = any>(
  config: AxiosRequestConfig<D>,
): Promise<T> => {
  return (await axiosInstance.request(config)).data;
};
axiosInstance.$get = async <T = any, D = any>(
  url: string,
  config?: AxiosRequestConfig<D>,
): Promise<T> => {
  return (await axiosInstance.get(url, config)).data;
};
axiosInstance.$delete = async <T = any, D = any>(
  url: string,
  config?: AxiosRequestConfig<D>,
): Promise<T> => {
  return (await axiosInstance.delete(url, config)).data;
};
axiosInstance.$head = async <T = any, D = any>(
  url: string,
  config?: AxiosRequestConfig<D>,
): Promise<T> => {
  return (await axiosInstance.head(url, config)).data;
};
axiosInstance.$options = async <T = any, D = any>(
  url: string,
  config?: AxiosRequestConfig<D>,
): Promise<T> => {
  return (await axiosInstance.options(url, config)).data;
};
axiosInstance.$post = async <T = any, D = any>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig<D>,
): Promise<T> => {
  return (await axiosInstance.post(url, data, config)).data;
};
axiosInstance.$put = async <T = any, D = any>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig<D>,
): Promise<T> => {
  return (await axiosInstance.put(url, data, config)).data;
};
axiosInstance.$patch = async <T = any, D = any>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig<D>,
): Promise<T> => {
  return (await axiosInstance.patch(url, data, config)).data;
};

export default axiosInstance;
